import { StatusFilter } from '../react/components/projects-views/status-filter-box';
import { projectStateAdapter } from '../react/state/project-state/project-state-adapter';
import { userStateAdapter } from '../react/state/user-state/user-state-adapter';
import { isDefined } from './variables';

type UserItemValue = {
  projectListSearchText: string;
  batchJobListSearchText: string;
  companyListSearchText: string;
  licenseListSearchText: string;
  companyAdminListSearchText: string;
  projectListStatusFilter: StatusFilter;
  projectListCompanyFilter: string;
  userListSearchFilter: string;
  placeMarkersFullResolution: 'true' | 'false';
};

type ProjectItemValue = {
  timelineListText: string;
};

type UserItemKey = keyof UserItemValue;
type ProjectItemKey = keyof ProjectItemValue;

function getUserItemKey(key: UserItemKey) {
  const userId = userStateAdapter.getSnapshot().getActiveUser()?.id;
  return `${userId ?? ''}_${key}`;
}

function getProjectItemKey(key: ProjectItemKey) {
  return `${projectStateAdapter.getSnapshot().project?.id}_${key}`;
}

/**
 * Sets an item in the session storage unique to the current user.
 */
function setUserItem<K extends UserItemKey>(key: K, value: UserItemValue[K]) {
  if (!userStateAdapter.getSnapshot().isLoaded) {
    return;
  }

  sessionStorage.setItem(getUserItemKey(key), value);
}

function setProjectItem<K extends ProjectItemKey>(key: K, value: ProjectItemValue[K]) {
  if (!isDefined(projectStateAdapter.getSnapshot().project)) {
    return;
  }

  sessionStorage.setItem(getProjectItemKey(key), value);
}

/**
 * Gets an item from the session storage unique for the current user.
 */
function getUserItemOrDefault<K extends UserItemKey>(
  key: K,
  defaultValue: UserItemValue[K],
): UserItemValue[K] {
  if (!userStateAdapter.getSnapshot().isLoaded) {
    return defaultValue;
  }

  const item = sessionStorage.getItem(getUserItemKey(key));
  return (item as UserItemValue[K]) ?? defaultValue; // Return the item or default value if null
}

/**
 * Gets an item from the session storage unique for the current user.
 */
function getProjectItemOrDefault<K extends ProjectItemKey>(
  key: K,
  defaultValue: ProjectItemValue[K],
): ProjectItemValue[K] {
  if (!isDefined(projectStateAdapter.getSnapshot().project)) {
    return defaultValue;
  }

  const item = sessionStorage.getItem(getProjectItemKey(key));
  return (item as ProjectItemValue[K]) ?? defaultValue; // Return the item or default value if null
}

export const sessionStorageUtils = {
  setUserItem,
  setProjectItem,
  getUserItemOrDefault,
  getProjectItemOrDefault,
};
